import React from 'react';

export const ModulesTab = ({ data, setData }) => {
  const addModule = React.useCallback(() => {
    setData(prev => ({
      ...prev,
      modules: {
        ...prev.modules,
        modules: [...(prev.modules?.modules || []), {
          id: Date.now(),
          nom: '',
          options: '',
          etatDeploiement: 'Non planifié',
          commentaires: ''
        }]
      }
    }));
  }, [setData]);

  const updateModule = React.useCallback((index, field, value) => {
    setData(prev => {
      const updatedModules = [...(prev.modules?.modules || [])];
      updatedModules[index] = {
        ...updatedModules[index],
        [field]: value
      };
      return {
        ...prev,
        modules: {
          ...prev.modules,
          modules: updatedModules
        }
      };
    });
  }, [setData]);

  const removeModule = React.useCallback((index) => {
    setData(prev => ({
      ...prev,
      modules: {
        ...prev.modules,
        modules: (prev.modules?.modules || []).filter((_, i) => i !== index)
      }
    }));
  }, [setData]);

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold text-blue-700">Modules déployés</h3>
        <button
          onClick={addModule}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
        >
          + Ajouter un module
        </button>
      </div>

      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
        <p className="text-sm text-yellow-800">
          <strong>Note :</strong> Vérifier s'il y a doublon avec l'onglet "identité projet"
        </p>
      </div>

      {data.modules && data.modules.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-gray-100">
                <th className="border border-gray-300 px-4 py-2 text-left">Modules</th>
                <th className="border border-gray-300 px-4 py-2 text-left">Options</th>
                <th className="border border-gray-300 px-4 py-2 text-left">État du déploiement</th>
                <th className="border border-gray-300 px-4 py-2 text-left">Commentaires</th>
                <th className="border border-gray-300 px-4 py-2 text-center w-16">Actions</th>
              </tr>
            </thead>
            <tbody>
              {data.modules.map((module, index) => (
                <tr key={module.id || index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                  <td className="border border-gray-300 px-4 py-2">
                    <input
                      type="text"
                      value={module.nom || ''}
                      onChange={(e) => updateModule(index, 'nom', e.target.value)}
                      className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Nom du module"
                    />
                  </td>
                  <td className="border border-gray-300 px-4 py-2">
                    <input
                      type="text"
                      value={module.options || ''}
                      onChange={(e) => updateModule(index, 'options', e.target.value)}
                      className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="ex: rappels rdv, reprise historique"
                    />
                  </td>
                  <td className="border border-gray-300 px-4 py-2">
                    <select
                      value={module.etatDeploiement || 'Non planifié'}
                      onChange={(e) => updateModule(index, 'etatDeploiement', e.target.value)}
                      className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="Non planifié">Non planifié</option>
                      <option value="Planifié">Planifié</option>
                      <option value="En cours">En cours</option>
                      <option value="Déployé">Déployé</option>
                      <option value="En production">En production</option>
                    </select>
                  </td>
                  <td className="border border-gray-300 px-4 py-2">
                    <textarea
                      value={module.commentaires || ''}
                      onChange={(e) => updateModule(index, 'commentaires', e.target.value)}
                      rows="2"
                      className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Si non planifié: date prévue. Si déployé: usages"
                    />
                  </td>
                  <td className="border border-gray-300 px-4 py-2 text-center">
                    <button
                      onClick={() => removeModule(index)}
                      className="text-red-600 hover:text-red-800"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="text-center py-8 bg-gray-50 rounded-lg border border-gray-200">
          <p className="text-gray-500 mb-4">Aucun module enregistré</p>
          <button
            onClick={addModule}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            Ajouter le premier module
          </button>
        </div>
      )}

      {/* Liste des modules disponibles pour référence */}
      <div className="mt-8 bg-gray-50 rounded-lg p-6 border border-gray-200">
        <h4 className="font-semibold text-gray-800 mb-3">Modules disponibles :</h4>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm text-gray-600">
          <div>• Suivi Connecté</div>
          <div>• Pré-admission</div>
          <div>• Borne d'accueil</div>
          <div>• Gestion documentaire</div>
          <div>• Questionnaires</div>
          <div>• Consentements</div>
          <div>• Rappels automatiques</div>
          <div>• Téléconsultation</div>
          <div>• Paiement en ligne</div>
        </div>
      </div>
    </div>
  );
};